import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import FormControl from '@zenbusiness/zui/FormControl';
import FormHelperText from '@zenbusiness/zui/FormHelperText';
import FormLabel from '@zenbusiness/zui/FormLabel';
import Slider from '@zenbusiness/zui/Slider';
import Text from '@zenbusiness/zui/Text';

/**
 * Range field using slider
 * @param {object} props - Component props
 * @param {object} props.def - Field definition
 * @param {number} props.value - Current slider value
 * @param {Function} props.onChange - Change handler
 * @param {boolean} [props.disabled=false] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 * @component
 */
function RangeField({ def, value, onChange, disabled = false, error }) {
  const handleChange = (event, newValue) => {
    onChange(newValue);
  };

  const fieldId = `range-${def.id}`;
  const hasError = Boolean(error);
  const currentValue = value ?? def.min ?? 0;

  // Format value for display
  const formatValue = (val) => {
    const prefix = def.format?.prefix || '';
    const suffix = def.format?.suffix || '';
    const formattedNumber = typeof val === 'number' ? val.toLocaleString() : val;
    return `${prefix}${formattedNumber}${suffix}`;
  };

  // Create marks for better UX
  const createMarks = () => {
    const marks = [];
    const range = def.max - def.min;
    const step = def.step || 1;

    // Add marks at min, max, and a few points in between
    marks.push({ value: def.min, label: formatValue(def.min) });

    if (range > step * 10) {
      // Add marks at 25%, 50%, 75% if range is large enough
      const quarter = def.min + Math.round((range * 0.25) / step) * step;
      const half = def.min + Math.round((range * 0.5) / step) * step;
      const threeQuarter = def.min + Math.round((range * 0.75) / step) * step;

      if (quarter !== def.min && quarter !== def.max) {
        marks.push({ value: quarter, label: formatValue(quarter) });
      }
      if (half !== def.min && half !== def.max) {
        marks.push({ value: half, label: formatValue(half) });
      }
      if (threeQuarter !== def.min && threeQuarter !== def.max) {
        marks.push({ value: threeQuarter, label: formatValue(threeQuarter) });
      }
    }

    marks.push({ value: def.max, label: formatValue(def.max) });
    return marks;
  };

  return (
    <FormControl
      disabled={disabled}
      error={hasError}
      fullWidth
      required={def.required}
      style={{ padding: '16px 0' }}
    >
      {def.label && (
        <FormLabel id={`${fieldId}-label`} required={def.required} style={{ marginBottom: '16px' }}>
          {def.label}
        </FormLabel>
      )}

      <Box
        sx={{ paddingLeft: (theme) => theme.spacing(2), paddingRight: (theme) => theme.spacing(2) }}
      >
        <Slider
          aria-labelledby={def.label ? `${fieldId}-label` : undefined}
          color="primary"
          disabled={disabled}
          marks={createMarks()}
          max={def.max}
          min={def.min}
          step={def.step || 1}
          value={currentValue}
          valueLabelDisplay="auto"
          valueLabelFormat={formatValue}
          onChange={handleChange}
        />
        <Text align="center" color="primary" gutterBottom>
          Current value: {formatValue(currentValue)}
        </Text>
      </Box>

      {(error || def.helpText) && (
        <FormHelperText style={{ marginTop: '8px' }}>{error || def.helpText}</FormHelperText>
      )}
    </FormControl>
  );
}

RangeField.propTypes = {
  def: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['range']).isRequired,
    label: PropTypes.string,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    step: PropTypes.number,
    format: PropTypes.shape({
      prefix: PropTypes.string,
      suffix: PropTypes.string
    })
  }).isRequired,
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string
};

export default RangeField;
